import { gql } from 'graphql-tag';
import { TypedDocumentNode } from '@apollo/client';

import {
  GetUserCatchesExcludingCatch,
  GetUserCatchesExcludingCatchVariables,
  GetCatchesInMapBoundingBox,
  GetCatchesInMapBoundingBoxVariables,
  GetCatchCountInMapBoundingBox,
  GetCatchCountInMapBoundingBoxVariables,
  GetSingleCatchForViewer,
  GetSingleCatchForViewerVariables,
} from '@generated/graphql';
import { CatchFragments } from '@graphql/fragments/rutilus/CatchType';
import { ImageFragments } from '@graphql/fragments/rutilus/ImageType';
import { PostFragments, CatchInMapPostFieldsFragment } from '@graphql/fragments/rutilus/PostType';
import { SpeciesFragments } from '@graphql/fragments/rutilus/SpeciesTypes';
import { UserFragments } from '@graphql/fragments/rutilus/UserType';
import { FishingWaterFragments } from '@graphql/fragments/rutilus/FishingWaterType';

export const getUserCatchesExcludingCatch: TypedDocumentNode<
  GetUserCatchesExcludingCatch,
  GetUserCatchesExcludingCatchVariables
> = gql`
  query GetUserCatchesExcludingCatch($externalId: String!, $excludeId: String!) {
    entry: user(externalId: $externalId) {
      catches(
        first: 6
        filters: { excludingCatch: $excludeId, withPhotos: true, orderByRecent: true }
      ) {
        edges {
          node {
            ...CatchMetadata
            user {
              ...UserId
              ...UserAvatar
              nickname
              isPremium
            }
            post {
              ...PostId
              images(first: 1, croppingStrategy: SMART, height: 400, width: 400) {
                edges {
                  node {
                    ...ImageFields
                  }
                }
                totalCount
              }
            }
          }
        }
      }
    }
  }
  ${CatchFragments.metadata}
  ${UserFragments.avatar}
  ${UserFragments.id}
  ${PostFragments.id}
  ${ImageFragments.imageFields}
`;

export const getCatchMetadata = gql`
  query GetCatchMetadata($externalId: String!) {
    entry: catch(externalId: $externalId) {
      ...CatchId
      species {
        ...SpeciesId
        displayName
      }
      fishingWater {
        ...FishingWaterId
        displayName
      }
      post {
        ...PostId
        text {
          text
        }
        images(first: 1, width: 960) {
          edges {
            node {
              ...ImageFields
            }
          }
        }
        ...PostVideoFields
        user {
          nickname
        }
      }
    }
  }
  ${PostFragments.id}
  ${PostFragments.videoFields}
  ${FishingWaterFragments.id}
  ${CatchFragments.id}
  ${SpeciesFragments.id}
`;

export const getPostIdByCatchId = gql`
  query GetPostIdByCatchId($externalId: String!) {
    entry: catch(externalId: $externalId) {
      ...CatchId
      post {
        ...PostId
        externalId
      }
    }
  }
  ${CatchFragments.id}
  ${PostFragments.id}
`;

export const getCatchesInMapBoundingBox: TypedDocumentNode<
  GetCatchesInMapBoundingBox,
  GetCatchesInMapBoundingBoxVariables
> = gql`
  query GetCatchesInMapBoundingBox(
    $boundingBox: BoundingBoxInputObject
    $first: Int
    $after: String
    $caughtInMonths: [MonthEnum!]
    $speciesIds: [String!]
  ) {
    mapArea(boundingBox: $boundingBox) {
      catches(
        first: $first
        after: $after
        caughtInMonths: $caughtInMonths
        speciesIds: $speciesIds
      ) {
        totalCount
        pageInfo {
          startCursor
          hasNextPage
          endCursor
        }
        edges {
          node {
            ...CatchId
            createdAt
            caughtAtGmt
            post {
              ...CatchInMapPostFields
            }
            species {
              ...SpeciesId
              displayName
            }
          }
        }
      }
    }
  }
  ${CatchFragments.id}
  ${SpeciesFragments.id}
  ${CatchInMapPostFieldsFragment}
`;

export const getCatchCountInMapBoundingBox: TypedDocumentNode<
  GetCatchCountInMapBoundingBox,
  GetCatchCountInMapBoundingBoxVariables
> = gql`
  query GetCatchCountInMapBoundingBox($boundingBox: BoundingBoxInputObject) {
    mapArea(boundingBox: $boundingBox) {
      catches {
        totalCount
      }
    }
  }
`;

export const getSingleCatchForViewer: TypedDocumentNode<
  GetSingleCatchForViewer,
  GetSingleCatchForViewerVariables
> = gql`
  query GetSingleCatchForViewer($externalId: String!) {
    catch(externalId: $externalId) {
      ...CatchId
      caughtAtGmt
      species {
        ...SpeciesId
        displayName
      }
      createdAt
      post {
        ...PostId
        displayProductUnits {
          totalCount
        }
        catch {
          ...CatchId
          ...CatchFishingWaterName
          ...CatchSpeciesName
        }
        comments {
          totalCount
        }
        createdAt
        ...PostProductUnits
        ...PostVideoFields
        images(first: 1, croppingStrategy: SMART, height: 400, width: 400) {
          totalCount
          edges {
            node {
              ...ImageFields
            }
          }
        }
        likedByCurrentUser
        likesCount
        text {
          text
        }
        user {
          ...UserAvatar
          ...UserId
          nickname
        }
      }
    }
  }
  ${CatchFragments.id}
  ${SpeciesFragments.id}
  ${PostFragments.id}
  ${CatchFragments.fishingWaterName}
  ${CatchFragments.speciesName}
  ${PostFragments.productUnits}
  ${PostFragments.videoFields}
  ${ImageFragments.imageFields}
  ${UserFragments.avatar}
  ${UserFragments.id}
`;
